// Phase E — Forwarder-side Customer Claims triage panel.
import React, { useState } from "react";
import { ShieldAlert, ChevronRight, Loader2 } from "lucide-react";
import { useClaims, useUpdateClaimStatus } from "../api/claims";

const mono = (size, color, weight, ls) => ({
  fontFamily: "'DM Mono', monospace",
  fontSize: size,
  color: color || "var(--text, #f0f2f8)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});

const STATUS_COLOR = {
  open: "#79b5f0",
  under_review: "#f3b14a",
  resolved: "#4ed084",
  rejected: "#ff8a8a",
};

const NEXT = {
  open: ["under_review", "rejected"],
  under_review: ["resolved", "rejected"],
};

const StatusPill = ({ status }) => {
  const c = STATUS_COLOR[status] || "#9aa1b3";
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded"
      style={{ background: `${c}22`, color: c, border: `1px solid ${c}55`, ...mono(10, c, 700, "0.6px"), textTransform: "uppercase" }}
    >
      {status.replace("_", " ")}
    </span>
  );
};

const ClaimRow = ({ claim }) => {
  const update = useUpdateClaimStatus();
  const [expanded, setExpanded] = useState(false);
  const [pendingTarget, setPendingTarget] = useState(null);
  const [note, setNote] = useState("");

  const apply = async () => {
    if (!pendingTarget) return;
    try {
      await update.mutateAsync({ claimId: claim.id, status: pendingTarget, note: note || null });
      setPendingTarget(null); setNote("");
    } catch (_) {
      // surface via mutation state; no extra UI side effect needed
    }
  };

  return (
    <div
      className="rounded-md mb-2"
      style={{ background: "var(--bg-surface, #ffffff)", border: "1px solid rgba(0,0,0,0.10)" }}
      data-testid={`fwd-claim-row-${claim.id}`}
    >
      <div className="flex items-start gap-3 p-3" style={{ cursor: "pointer" }} onClick={() => setExpanded((v) => !v)}>
        <ShieldAlert size={14} color="#E24B4A" className="mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <strong style={mono(12)}>{claim.claim_type_label || claim.claim_type}</strong>
            <StatusPill status={claim.status} />
            <span style={mono(10, "var(--text-dim, #6b7388)")}>{(claim.created_at || "").slice(0, 16).replace("T", " ")}</span>
          </div>
          <div className="mt-1" style={{ ...mono(11, "var(--text-dim, #6b7388)"), overflow: "hidden", textOverflow: "ellipsis", whiteSpace: expanded ? "normal" : "nowrap" }}>
            {claim.description}
          </div>
        </div>
        <ChevronRight size={14} style={{ transform: expanded ? "rotate(90deg)" : "rotate(0)", transition: "transform 120ms" }} />
      </div>
      {expanded && (
        <div className="px-3 pb-3 border-t" style={{ borderColor: "rgba(0,0,0,0.06)" }} data-testid={`fwd-claim-expanded-${claim.id}`}>
          {(claim.evidence || []).length > 0 && (
            <div className="mt-3">
              <div style={mono(9, "var(--text-dim, #6b7388)", 700, "0.8px")}>EVIDENCE</div>
              <ul className="mt-1 space-y-1">
                {claim.evidence.map((e) => (
                  <li key={e.id} style={mono(11)}>
                    📎 {e.original_name} <span style={mono(9, "var(--text-dim, #6b7388)")}>· {((e.size || 0) / 1024).toFixed(0)} KB</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(NEXT[claim.status] || []).length > 0 && (
            <div className="mt-3">
              <div style={mono(9, "var(--text-dim, #6b7388)", 700, "0.8px")}>TRIAGE</div>
              {!pendingTarget ? (
                <div className="mt-1 flex gap-2 flex-wrap">
                  {NEXT[claim.status].map((target) => (
                    <button
                      key={target}
                      type="button"
                      onClick={() => setPendingTarget(target)}
                      className="px-2 py-1 rounded"
                      style={{ background: STATUS_COLOR[target] + "22", color: STATUS_COLOR[target], border: `1px solid ${STATUS_COLOR[target]}55`, ...mono(10, STATUS_COLOR[target], 700, "0.6px"), textTransform: "uppercase", cursor: "pointer" }}
                      data-testid={`fwd-claim-${claim.id}-to-${target}`}
                    >
                      → {target.replace("_", " ")}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="mt-2 space-y-2">
                  <textarea
                    rows={2}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Internal note (not visible to customer)"
                    style={{ width: "100%", padding: 8, border: "1px solid rgba(0,0,0,0.15)", borderRadius: 4, ...mono(11) }}
                    data-testid={`fwd-claim-${claim.id}-note`}
                  />
                  <div className="flex gap-2 justify-end">
                    <button type="button" onClick={() => { setPendingTarget(null); setNote(""); }} style={{ background: "transparent", border: "1px solid rgba(0,0,0,0.15)", padding: "4px 8px", borderRadius: 4, ...mono(10) }}>Cancel</button>
                    <button
                      type="button"
                      onClick={apply}
                      disabled={update.isPending}
                      style={{ background: STATUS_COLOR[pendingTarget], color: "#fff", border: "none", padding: "5px 10px", borderRadius: 4, ...mono(10, "#fff", 700, "0.8px"), textTransform: "uppercase", cursor: "pointer", opacity: update.isPending ? 0.6 : 1 }}
                      data-testid={`fwd-claim-${claim.id}-confirm`}
                    >
                      {update.isPending ? <Loader2 size={11} className="animate-spin inline" /> : null}
                      Mark {pendingTarget.replace("_", " ")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="mt-3">
            <div style={mono(9, "var(--text-dim, #6b7388)", 700, "0.8px")}>HISTORY</div>
            <ol className="mt-1 space-y-0.5" style={mono(10, "var(--text-dim, #6b7388)")}>
              {(claim.status_history || []).map((h, i) => (
                <li key={i}>
                  • {(h.at || "").slice(0, 16).replace("T", " ")} → <strong>{h.status}</strong>
                  {h.note ? ` — ${h.note}` : ""}
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </div>
  );
};

export const CustomerClaimsPanel = ({ logisticsRequestId }) => {
  const { data, isLoading } = useClaims({ logistics_request_id: logisticsRequestId });
  const items = data?.items || [];

  return (
    <section
      className="rounded-md p-4 mt-4"
      style={{ background: "var(--bg-surface, #ffffff)", border: "1px solid rgba(226,75,74,0.30)" }}
      data-testid="customer-claims-panel"
    >
      <header className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 15 }} className="flex items-center gap-2">
          <ShieldAlert size={16} color="#E24B4A" />
          Customer claims ({items.length})
        </h2>
      </header>
      {isLoading && <div style={mono(11, "var(--text-dim, #6b7388)")}>Loading…</div>}
      {!isLoading && items.length === 0 && (
        <div className="text-center py-4" style={mono(11, "var(--text-dim, #6b7388)")} data-testid="customer-claims-empty">
          No claims on this deal.
        </div>
      )}
      {items.map((c) => <ClaimRow key={c.id} claim={c} />)}
    </section>
  );
};

export default CustomerClaimsPanel;
